import { useDebounce } from 'lib-knowledge-delivery-common/hooks/useDebounce';
import { useEffect, useState } from 'react';
import { StringParam, useQueryParams } from 'use-query-params';

const suggestionsSearchParams = { search: StringParam };

/**
 * Search term for the suggestions list, debounced and kept in the URL so the
 * view survives a reload and can be linked to.
 *
 * A sibling of `hooks/useDebouncedSearchValueInUrl`, which cannot be reused
 * here: it writes through `useArticlesFilters`, i.e. the articles list's own
 * filter store.
 */
export function useSuggestionsSearch(): {
  /** Current input value, updated on every keystroke. */
  instantSearchValue: string;
  /** Debounced value to send to the API — `undefined` when empty. */
  searchQuery: string | undefined;
  onChange: (value: string) => void;
} {
  const [params, setParams] = useQueryParams(suggestionsSearchParams);
  const [instantSearchValue, setInstantSearchValue] = useState(
    params.search ?? '',
  );
  const debouncedSearchValue = useDebounce(instantSearchValue, 1000);

  useEffect(() => {
    // `undefined` drops the param rather than leaving `?search=` behind.
    setParams({ search: debouncedSearchValue || undefined }, 'replaceIn');
  }, [setParams, debouncedSearchValue]);

  return {
    instantSearchValue,
    // The API types this as NonEmptyString, so an empty search must be omitted.
    searchQuery: debouncedSearchValue || undefined,
    onChange: setInstantSearchValue,
  };
}
